import React from 'react';

const API = '/api/opo/marketing';

function previewModeForDraft(d) {
  const g = d.metadata?.graphic?.preview_mode;
  if (g) return g;
  const p = (d.platform || '').toLowerCase();
  const f = (d.format || 'post').toLowerCase();
  if (p === 'opo_news') return 'opo_news';
  if (p === 'tiktok') return 'tiktok_feed';
  if (p === 'linkedin' && (f === 'article' || f === 'longform')) return 'linkedin_article';
  return 'linkedin_post';
}

function formatLabel(mode) {
  if (mode === 'opo_news') return 'OPO NEWS preview';
  if (mode === 'tiktok_feed') return 'TikTok feed preview';
  if (mode === 'linkedin_article') return 'LinkedIn article preview';
  return 'LinkedIn post preview';
}

function assetUrl(assetId) {
  if (!assetId) return null;
  return `${API}/assets/${assetId}/file`;
}

function clip(text, max, full) {
  const t = text || '';
  if (full || t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

/** Renders the draft's attached asset — <video> when the asset is a video, else <img>. */
function AssetMedia({ src, isVideo, className }) {
  if (!src) return null;
  if (isVideo) {
    return <video src={src} className={className} controls muted playsInline style={{ width: '100%', display: 'block' }} />;
  }
  return <img src={src} alt="" className={className} />;
}

function TikTokPreview({ draft, imageSrc, full, isVideo }) {
  const body = draft.body || '';
  const tags = (draft.hashtags || []).map((h) => `#${String(h).replace(/^#+/, '')}`).join(' ');
  return (
    <div className="mkt-preview-frame mkt-preview-tiktok">
      <div className="mkt-preview-tiktok-phone">
        <div className="mkt-preview-tiktok-top">Following · For You</div>
        <div className="mkt-preview-tiktok-media">
          {imageSrc ? (
            <AssetMedia src={imageSrc} isVideo={isVideo} className="mkt-preview-tiktok-img" />
          ) : (
            <div className="mkt-preview-tiktok-placeholder">Generate GRAPHIC or VIDEO first</div>
          )}
          <div className="mkt-preview-tiktok-overlay">
            <strong>@{draft.brand_name || 'brand'}</strong>
            <p>{clip(body, 220, full)}</p>
            {tags && <span className="mkt-preview-tags">{tags}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkedInPostPreview({ draft, imageSrc, full, isVideo }) {
  return (
    <div className="mkt-preview-frame mkt-preview-linkedin">
      <div className="mkt-preview-li-card">
        <div className="mkt-preview-li-header">
          <span className="mkt-preview-li-avatar" />
          <div>
            <strong>{draft.brand_name || 'Brand'}</strong>
            <span className="mkt-preview-li-meta">Founder · 1h · 🌐</span>
          </div>
        </div>
        {draft.title && <p className="mkt-preview-li-title">{draft.title}</p>}
        <p className="mkt-preview-li-body mkt-preview-li-body-pre">{clip(draft.body, 400, full)}</p>
        {imageSrc && (
          <div className="mkt-preview-li-image-wrap">
            <AssetMedia src={imageSrc} isVideo={isVideo} className="mkt-preview-li-image" />
          </div>
        )}
        {(draft.hashtags || []).length > 0 && (
          <p className="mkt-preview-tags">{(draft.hashtags || []).map((h) => `#${String(h).replace(/^#+/, '')}`).join(' ')}</p>
        )}
        <div className="mkt-preview-li-actions">Like · Comment · Repost · Send</div>
      </div>
    </div>
  );
}

function OpoNewsPreview({ draft, imageSrc, full, isVideo }) {
  const tags = (draft.hashtags || []).map((h) => `#${String(h).replace(/^#+/, '')}`).join(' ');
  return (
    <div className="mkt-preview-frame mkt-preview-opo-news">
      <div className="mkt-preview-opo-news-card">
        <div className="mkt-preview-opo-news-head">
          <span className="mkt-preview-opo-news-badge">OPO NEWS</span>
          <span className="mkt-preview-opo-news-brand">{draft.brand_name || 'OptiPrime'}</span>
        </div>
        {draft.title && <h4 className="mkt-preview-opo-news-title">{draft.title}</h4>}
        {draft.hook && <p className="mkt-preview-opo-news-hook">{draft.hook}</p>}
        {imageSrc && (
          <div className="mkt-preview-opo-news-image-wrap">
            <AssetMedia src={imageSrc} isVideo={isVideo} className="mkt-preview-opo-news-image" />
          </div>
        )}
        <p className="mkt-preview-opo-news-body mkt-preview-li-body-pre">{clip(draft.body, 800, full)}</p>
        {tags && <p className="mkt-preview-tags">{tags}</p>}
        <p className="mkt-preview-opo-news-foot">
          Preview only — click <strong>Publish</strong> in Studio to deliver to OPO → OPO News in Messages and the feed on AI Navigation.
        </p>
      </div>
    </div>
  );
}

function LinkedInArticlePreview({ draft, imageSrc, full, isVideo }) {
  return (
    <div className="mkt-preview-frame mkt-preview-linkedin mkt-preview-li-article">
      <div className="mkt-preview-li-card">
        <span className="mkt-preview-li-badge">Article</span>
        <div className="mkt-preview-li-header">
          <span className="mkt-preview-li-avatar" />
          <div>
            <strong>{draft.brand_name || 'Brand'}</strong>
            <span className="mkt-preview-li-meta">Published on LinkedIn · 5 min read</span>
          </div>
        </div>
        <h4 className="mkt-preview-li-article-title">{draft.title}</h4>
        {imageSrc && (
          <div className="mkt-preview-li-article-hero">
            <AssetMedia src={imageSrc} isVideo={isVideo} />
          </div>
        )}
        <p className="mkt-preview-li-body mkt-preview-li-body-pre">{clip(draft.body, 600, full)}</p>
      </div>
    </div>
  );
}

export default function MarketingPlatformPreview({ draft, onClose }) {
  if (!draft) return null;
  const meta = typeof draft.metadata === 'string' ? (() => { try { return JSON.parse(draft.metadata); } catch { return {}; } })() : (draft.metadata || {});
  const mode = previewModeForDraft({ ...draft, metadata: meta });
  const imageSrc = assetUrl(draft.asset_id);
  const isVideo = draft.asset_kind === 'video';
  const graphic = meta.graphic;
  const video = meta.video;

  return (
    <div className="mkt-preview-backdrop" role="presentation" onClick={onClose}>
      <div className="mkt-preview-modal" role="dialog" aria-label="Platform preview" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-preview-modal-head">
          <h4>{formatLabel(mode)}</h4>
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={onClose}>CLOSE</button>
        </div>
        {mode === 'opo_news' && <OpoNewsPreview draft={draft} imageSrc={imageSrc} isVideo={isVideo} full />}
        {mode === 'tiktok_feed' && <TikTokPreview draft={draft} imageSrc={imageSrc} isVideo={isVideo} />}
        {mode === 'linkedin_article' && <LinkedInArticlePreview draft={draft} imageSrc={imageSrc} isVideo={isVideo} />}
        {mode === 'linkedin_post' && <LinkedInPostPreview draft={draft} imageSrc={imageSrc} isVideo={isVideo} />}
        {graphic?.image_prompt && (
          <details className="mkt-preview-prompt-details">
            <summary>Image prompt used</summary>
            <p>{graphic.image_prompt}</p>
            {graphic.negative_prompt && <p className="mkt-preview-neg"><em>Negative:</em> {graphic.negative_prompt}</p>}
          </details>
        )}
        {video?.video_prompt && (
          <details className="mkt-preview-prompt-details">
            <summary>Video prompt used</summary>
            <p>{video.video_prompt}</p>
            {video.negative_prompt && <p className="mkt-preview-neg"><em>Negative:</em> {video.negative_prompt}</p>}
          </details>
        )}
      </div>
    </div>
  );
}

export {
  previewModeForDraft,
  formatLabel,
  assetUrl,
  TikTokPreview,
  LinkedInPostPreview,
  LinkedInArticlePreview,
  OpoNewsPreview,
};
